import React, { useEffect, useState } from "react";

const API_KEY      = "SXai-6sxnvPBZ4vuHddTvFz20Kd3VEk-r1hcERMcM_c";
const BASE         = "https://pwg.clay.in/api/external";
const USAGE_URL    = "https://pwg.clay.in/api/Pwg/QueryUsage";
const COVERAGE_URL = "https://pwg.clay.in/api/Pwg/GetCoverage2";
const HEADERS      = { "accept": "*/*", "X-API-Key": API_KEY, "Content-Type": "application/json" };

// ─── Plan Options ─────────────────────────────────────────────────────────────

const PLAN_OPTIONS = [
  { id: "01", label: "(01) 1 GB — 30 Days" },
  { id: "02", label: "(02) 2.5 GB — 30 Days" },
  { id: "03", label: "(03) 5 GB — 30 Days" },
  { id: "04", label: "(04) 7.5 GB — 30 Days" },
  { id: "05", label: "(05) 10 GB — 30 Days" },
  { id: "06", label: "(06) 15 GB — 30 Days" },
  { id: "07", label: "(07) 20 GB — 30 Days" },
  { id: "08", label: "(08) 1 GB — 30 Days" },
  { id: "09", label: "(09) 2.5 GB — 30 Days" },
  { id: "10", label: "(10) 3 GB — 30 Days" },
  { id: "11", label: "(11) 5 GB — 30 Days" },
  { id: "12", label: "(12) 10 GB — 30 Days" },
  { id: "13", label: "(13) 25 GB — 30 Days" },
  { id: "14", label: "(14) 30 GB — 30 Days" },
  { id: "15", label: "(15) 40 GB — 30 Days" },
  { id: "16", label: "(16) 50 GB — 30 Days" },
  { id: "17", label: "(17) 2 GB — 30 Days" },
  { id: "18", label: "(18) 4 GB — 30 Days" },
  { id: "19", label: "(19) 6 GB — 30 Days" },
  { id: "20", label: "(20) 8 GB — 30 Days" },
  { id: "21", label: "(21) 12 GB — 30 Days" },
  { id: "22", label: "(22) 100 GB — 30 Days" },
  { id: "23", label: "(23) Unlimited — 30 Days" },
  { id: "24", label: "(24) Unlimited Plus — 30 Days" },
  { id: "25", label: "(25) 1 GB — 7 Days" },
  { id: "26", label: "(26) 3 GB — 7 Days" },
  { id: "27", label: "(27) 5 GB — 7 Days" },
  { id: "28", label: "(28) 500 MB — 30 Days" },
  { id: "29", label: "(29) 250 MB — 30 Days" },
  { id: "30", label: "(30) 75 GB — 30 Days" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const safe = (val) => {
  if (val === null || val === undefined) return "—";
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const formatTimestamp = (ts) => {
  if (!ts || ts.length < 14) return safe(ts);
  try {
    const y = ts.slice(0,4), mo = ts.slice(4,6), d = ts.slice(6,8);
    const h = ts.slice(8,10), mi = ts.slice(10,12), s = ts.slice(12,14);
    return new Date(`${y}-${mo}-${d}T${h}:${mi}:${s}Z`).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return safe(ts); }
};

// ─── Reusable Components ──────────────────────────────────────────────────────

const Modal = ({ title, onClose, children, maxWidth = "max-w-md" }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
    <div className={`bg-white rounded-2xl shadow-2xl w-full ${maxWidth} max-h-[90vh] flex flex-col`}>
      <div className="flex justify-between items-center px-6 py-4 border-b shrink-0">
        <h2 className="font-semibold text-gray-800">{title}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-lg leading-none">✕</button>
      </div>
      <div className="px-6 py-4 overflow-y-auto">{children}</div>
    </div>
  </div>
);

const Field = ({ label, name, value, onChange, required, readOnly, placeholder }) => (
  <div className="mb-3">
    <label className="block text-xs font-medium text-gray-500 mb-1">
      {label}{required && " *"}
    </label>
    <input
      name={name}
      value={value ?? ""}
      onChange={onChange}
      required={required}
      readOnly={readOnly}
      placeholder={placeholder}
      className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${readOnly ? "bg-gray-50 text-gray-500" : ""}`}
    />
  </div>
);

const SelectField = ({ label, name, value, onChange, options, required, placeholder = "Select plan ID" }) => (
  <div className="mb-3">
    <label className="block text-xs font-medium text-gray-500 mb-1">
      {label}{required && " *"}
    </label>
    <div className="relative">
      <select
        name={name}
        value={value ?? ""}
        onChange={onChange}
        className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 appearance-none bg-white pr-8 ${!value ? "text-gray-400" : "text-gray-800"}`}
      >
        <option value="" disabled>{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>{opt.label}</option>
        ))}
      </select>
      {/* Custom chevron */}
      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2 mt-1">{children}</p>
);

const StatusMsg = ({ status }) =>
  status ? (
    <p className={`text-sm mb-3 ${status.ok ? "text-green-600" : "text-red-500"}`}>{status.msg}</p>
  ) : null;

const ModalFooter = ({ onClose, onSubmit, loading, label, color = "bg-blue-600" }) => (
  <div className="flex gap-2 justify-end mt-4">
    <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm text-gray-600 hover:bg-gray-50">
      Cancel
    </button>
    <button
      onClick={onSubmit}
      disabled={loading}
      className={`px-4 py-2 rounded-lg text-white text-sm ${color} disabled:opacity-50 hover:opacity-90 transition`}
    >
      {loading ? "Processing…" : label}
    </button>
  </div>
);

// ─── API Helper ───────────────────────────────────────────────────────────────

const callApi = async (endpoint, body) => {
  const res  = await fetch(`${BASE}/${endpoint}`, { method: "POST", headers: HEADERS, body: JSON.stringify({ request: body }) });
  const data = await res.json();
  if (data.response?.status === "success") return { ok: true, msg: "Operation successful!" };
  const errMsg = data.response?.errors?.firstError?.message || data.response?.status || "Request failed";
  return { ok: false, msg: errMsg };
};

const useApiCall = () => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus]   = useState(null);
  const submit = async (endpoint, body) => {
    setLoading(true); setStatus(null);
    try { setStatus(await callApi(endpoint, body)); }
    catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally { setLoading(false); }
  };
  return { loading, status, submit };
};

// ─── Swap Modal ───────────────────────────────────────────────────────────────

const SwapModal = ({ item, onClose }) => {
  const [form, setForm] = useState({ type: "SwapESN", mdn: item?.mdn ?? "", oldEsn: item?.esn ?? "", newEsn: "" });
  const { loading, status, submit } = useApiCall();
  const ch = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  return (
    <Modal title="Swap ESN" onClose={onClose}>
      <Field label="MDN"     name="mdn"    value={form.mdn}    onChange={ch} required />
      <Field label="Old ESN" name="oldEsn" value={form.oldEsn} onChange={ch} required />
      <Field label="New ESN" name="newEsn" value={form.newEsn} onChange={ch} required />
      <StatusMsg status={status} />
      <ModalFooter onClose={onClose} onSubmit={() => submit("SwapEsn", form)} loading={loading} label="Swap" />
    </Modal>
  );
};

// ─── Deactivate Modal ─────────────────────────────────────────────────────────

const DeactivateModal = ({ item, onClose }) => {
  const [form, setForm] = useState({ type: "Disconnect", mdn: item?.mdn ?? "", esn: item?.esn ?? "" });
  const { loading, status, submit } = useApiCall();
  const ch = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  return (
    <Modal title="Deactivate Line" onClose={onClose}>
      <p className="text-sm text-gray-500 mb-4">This will disconnect the line. Confirm the details below.</p>
      <Field label="MDN"  name="mdn"  value={form.mdn}  onChange={ch} required />
      <Field label="ESN"  name="esn"  value={form.esn}  onChange={ch} required />
      <Field label="Type" name="type" value={form.type} onChange={ch} />
      <StatusMsg status={status} />
      <ModalFooter onClose={onClose} onSubmit={() => submit("Disconnect", form)} loading={loading} label="Deactivate" color="bg-red-600" />
    </Modal>
  );
};

// ─── Reconnect Modal ──────────────────────────────────────────────────────────

const ReconnectModal = ({ item, onClose }) => {
  const [form, setForm] = useState({
    type: "Reconnect", mdn: item?.mdn ?? "", esn: item?.esn ?? "",
    imei: "", plan: item?.planId ?? "", zip: "",
    billingCode: item?.billingCode ?? "", stateCode: "",
    e911Address: { street1: "123 Main St", street2: "JPEX Testing", city: "New York", state: "NY", zip: "942105" },
  });
  const { loading, status, submit } = useApiCall();
  const ch = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  return (
    <Modal title="Reconnect Line" onClose={onClose}>
      <div className="grid grid-cols-2 gap-x-3">
        <Field label="MDN"          name="mdn"         value={form.mdn}         onChange={ch} required />
        <Field label="ESN"          name="esn"         value={form.esn}         onChange={ch} required />
        <Field label="IMEI"         name="imei"        value={form.imei}        onChange={ch} />
        <Field label="Plan"         name="plan"        value={form.plan}        onChange={ch} />
        <Field label="ZIP"          name="zip"         value={form.zip}         onChange={ch} />
        <Field label="Billing Code" name="billingCode" value={form.billingCode} onChange={ch} />
        <Field label="State Code"   name="stateCode"   value={form.stateCode}   onChange={ch} />
        <Field label="Type"         name="type"        value={form.type}        onChange={ch} />
      </div>
      <StatusMsg status={status} />
      <ModalFooter onClose={onClose} onSubmit={() => submit("Reconnect", form)} loading={loading} label="Reconnect" color="bg-green-600" />
    </Modal>
  );
};

// ─── Usage Modal ──────────────────────────────────────────────────────────────

const UsageRow = ({ label, value, highlight }) => (
  <div className={`flex justify-between items-center py-2 px-3 rounded-lg mb-1 ${highlight ? "bg-blue-50" : "bg-gray-50"}`}>
    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
    <span className={`text-sm font-semibold ${highlight ? "text-blue-700" : "text-gray-800"}`}>{safe(value)}</span>
  </div>
);

const StatusBadge = ({ value }) => {
  if (!value || value === "—") return <span className="text-sm text-gray-400">—</span>;
  const isActive = value.toUpperCase() === "ACTIVE" || value.toUpperCase() === "SUCCESS";
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
      {value}
    </span>
  );
};

const parseToMB = (str) => {
  if (!str) return null;
  const s = str.trim(), num = parseFloat(s);
  if (isNaN(num)) return null;
  if (s.includes("GB")) return num * 1024;
  if (s.includes("KB")) return num / 1024;
  if (s.includes("B") && !s.includes("MB") && !s.includes("GB") && !s.includes("KB")) return num / (1024 * 1024);
  return num;
};

const UsageBar = ({ limit, used }) => {
  const limitMB = parseToMB(limit), usedMB = parseToMB(used);
  if (limitMB === null || usedMB === null || limitMB === 0) return null;
  const pct   = Math.min(100, Math.round((usedMB / limitMB) * 100));
  const color = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-yellow-500" : "bg-purple-500";
  return (
    <div className="bg-purple-50 rounded-xl p-4 mb-3">
      <div className="flex justify-between items-end mb-2">
        <div>
          <p className="text-xs text-purple-400 font-medium uppercase tracking-wide">Data Used</p>
          <p className="text-2xl font-bold text-purple-700">{pct}%</p>
        </div>
        <div className="text-right text-xs text-gray-500">
          <p><span className="font-semibold text-purple-600">{safe(used)}</span> used</p>
          <p>of <span className="font-semibold">{safe(limit)}</span></p>
        </div>
      </div>
      <div className="w-full bg-purple-200 rounded-full h-2.5">
        <div className={`h-2.5 rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const UsageModal = ({ item, onClose }) => {
  const [esn, setEsn]             = useState(item?.esn ?? "");
  const [mdn, setMdn]             = useState(item?.mdn ?? "");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [fetched, setFetched]     = useState(false);

  const fetchUsage = async () => {
    if (!esn || !mdn) { setError("ESN and MDN are required."); return; }
    setLoading(true); setError(null); setUsageData(null); setFetched(false);
    try {
      const res  = await fetch(USAGE_URL, { method: "POST", headers: { "accept": "*/*", "Content-Type": "application/json" }, body: JSON.stringify({ request: { type: "QueryUsage", esn, mdn } }) });
      const json = await res.json();
      if (json.success && json.data?.status === "success") setUsageData(json.data);
      else if (json.success && json.data?.status === "failure") setError("Query returned failure — no usage data found.");
      else setError("Unexpected response from server.");
    } catch (e) { setError(e.message || "Network error."); }
    finally { setLoading(false); setFetched(true); }
  };

  useEffect(() => { if (esn && mdn) fetchUsage(); }, []); // eslint-disable-line

  return (
    <Modal title="📊 Usage Details" onClose={onClose} maxWidth="max-w-lg">
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">ESN / SIM *</label>
          <input value={esn} onChange={(e) => setEsn(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">MDN *</label>
          <input value={mdn} onChange={(e) => setMdn(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
        </div>
      </div>
      <button onClick={fetchUsage} disabled={loading} className="w-full mb-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50 transition">
        {loading ? "Querying…" : "Query Usage"}
      </button>
      {error && <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3"><span className="text-red-500 mt-0.5">⚠️</span><p className="text-sm text-red-600">{error}</p></div>}
      {usageData && (
        <div>
          <UsageBar limit={usageData.limit} used={usageData.used} />
          <div className="grid grid-cols-2 gap-1 mt-2">
            <UsageRow label="MDN"        value={usageData.mdn}       highlight />
            <UsageRow label="SIM"        value={usageData.sim} />
            <UsageRow label="SOC / Plan" value={usageData.soc} />
            <UsageRow label="Usage Type" value={usageData.usageType} />
            <div className="flex justify-between items-center py-2 px-3 rounded-lg mb-1 bg-gray-50">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Account Status</span>
              <StatusBadge value={usageData.accountStatus} />
            </div>
            <div className="flex justify-between items-center py-2 px-3 rounded-lg mb-1 bg-gray-50">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Usage Status</span>
              <StatusBadge value={usageData.usageStatus} />
            </div>
            <UsageRow label="Limit" value={usageData.limit} highlight />
            <UsageRow label="Used"  value={usageData.used}  highlight />
            <div className="col-span-2">
              <div className="flex justify-between items-center py-2 px-3 rounded-lg mb-1 bg-gray-50">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status Code</span>
                <StatusBadge value={usageData.statusCode} />
              </div>
            </div>
            {usageData.description && <div className="col-span-2"><UsageRow label="Description" value={usageData.description} /></div>}
            <div className="col-span-2"><UsageRow label="Timestamp" value={formatTimestamp(usageData.timestamp)} /></div>
          </div>
        </div>
      )}
      {fetched && !usageData && !error && <p className="text-center text-gray-400 text-sm py-4">No data returned.</p>}
      <div className="flex justify-end mt-4">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm text-gray-600 hover:bg-gray-50">Close</button>
      </div>
    </Modal>
  );
};

// ─── Coverage Modal ───────────────────────────────────────────────────────────

const CoverageModal = ({ onClose }) => {
  const [zip, setZip]         = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [result, setResult]   = useState(null);

  const fetchCoverage = async () => {
    const trimmed = zip.trim();
    if (!trimmed) { setError("Please enter a ZIP code."); return; }
    setLoading(true); setError(null); setResult(null);
    try {
      const res  = await fetch(COVERAGE_URL, { method: "POST", headers: { "accept": "*/*", "Content-Type": "application/json" }, body: JSON.stringify({ request: { type: "GetCoverage2", zip: trimmed } }) });
      const json = await res.json();
      if (json.success && json.data) setResult(json.data);
      else setError("Unexpected response from server.");
    } catch (e) { setError(e.message || "Network error."); }
    finally { setLoading(false); }
  };

  const isEligible = result?.activationEligible?.toLowerCase() === "true";

  return (
    <Modal title="📡 Check Coverage" onClose={onClose} maxWidth="max-w-sm">
      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-500 mb-1">ZIP Code *</label>
        <div className="flex gap-2">
          <input value={zip} onChange={(e) => setZip(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchCoverage()} placeholder="e.g. 941025" maxLength={10}
            className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
          <button onClick={fetchCoverage} disabled={loading} className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50 transition whitespace-nowrap">
            {loading ? "Checking…" : "Check"}
          </button>
        </div>
      </div>
      {error && <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3"><span className="text-red-500 mt-0.5">⚠️</span><p className="text-sm text-red-600">{error}</p></div>}
      {result && (
        <div className="rounded-xl border overflow-hidden">
          <div className={`px-5 py-4 flex items-center gap-3 ${isEligible ? "bg-green-50 border-b border-green-100" : "bg-orange-50 border-b border-orange-100"}`}>
            <span className="text-2xl">{isEligible ? "✅" : "❌"}</span>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Activation Eligible</p>
              <p className={`text-lg font-bold ${isEligible ? "text-green-700" : "text-orange-600"}`}>{isEligible ? "Yes — Eligible" : "No — Not Eligible"}</p>
            </div>
          </div>
          <div className="divide-y bg-white">
            {[{ label: "ZIP Code", value: result.zip }, { label: "Status", value: result.status, badge: true }, { label: "Status Code", value: result.statusCode }, { label: "Timestamp", value: formatTimestamp(result.timestamp) }]
              .map(({ label, value, badge }) => (
                <div key={label} className="flex justify-between items-center px-4 py-2.5">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
                  {badge
                    ? <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${String(value).toLowerCase() === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>{safe(value)}</span>
                    : <span className="text-sm font-semibold text-gray-800">{safe(value)}</span>}
                </div>
              ))}
          </div>
        </div>
      )}
      <div className="flex justify-end mt-4">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm text-gray-600 hover:bg-gray-50">Close</button>
      </div>
    </Modal>
  );
};

// ─── Purchase Plan Modal ──────────────────────────────────────────────────────

const PurchasePlanModal = ({ item, onClose }) => {
  const [form, setForm] = useState({
    type:   "Purchase",
    mdn:    item?.mdn    ?? "",
    planId: "",                    // always start blank so user selects
    e911ADDRESS: { streeT1: "", streeT2: "", city: "", state: "", zip: "" },
  });
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);

  const chTop  = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const chAddr = (e) => setForm((f) => ({ ...f, e911ADDRESS: { ...f.e911ADDRESS, [e.target.name]: e.target.value } }));

  // selected plan label for display
  const selectedPlan = PLAN_OPTIONS.find((p) => p.id === form.planId);

  const handleSubmit = async () => {
    if (!form.mdn)    { setResult({ ok: false, msg: "MDN is required." });     return; }
    if (!form.planId) { setResult({ ok: false, msg: "Please select a plan." }); return; }
    setLoading(true); setResult(null);
    try {
      const res  = await fetch(`${BASE}/PurchasePlan`, { method: "POST", headers: HEADERS, body: JSON.stringify({ request: form }) });
      const json = await res.json();
      if (json.success && json.data?.status === "success") {
        setResult({ ok: true, purchaseId: json.data.purchaseId, timestamp: json.data.timestamp, msg: "Plan purchased successfully!" });
        return;
      }
      const msg = json.data?.status === "failure"
        ? `Purchase failed — status code: ${safe(json.data?.statusCode ?? "unknown")}`
        : json.message || "Request failed";
      setResult({ ok: false, msg, timestamp: json.data?.timestamp });
    } catch (e) {
      setResult({ ok: false, msg: e.message || "Network error." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="🛒 Purchase Plan" onClose={onClose} maxWidth="max-w-lg">

      {/* ── Line Details ── */}
      <SectionLabel>Line Details</SectionLabel>
      <div className="grid grid-cols-2 gap-x-3">
        <Field label="MDN" name="mdn" value={form.mdn} onChange={chTop} required />

        {/* Plan dropdown — full width */}
        <div className="col-span-1 mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">Select Plan *</label>
          <div className="relative">
            <select
              name="planId"
              value={form.planId}
              onChange={chTop}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 appearance-none bg-white pr-8 ${!form.planId ? "text-gray-400" : "text-gray-800"}`}
            >
              <option value="" disabled>Select plan ID</option>
              {PLAN_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Type — full width row */}
        <div className="col-span-2">
          <Field label="Type" name="type" value={form.type} onChange={chTop} />
        </div>
      </div>

      {/* ── Selected plan summary badge ── */}
      {selectedPlan && (
        <div className="mb-3 flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
          <span className="text-indigo-500 text-base">📋</span>
          <div>
            <p className="text-xs text-indigo-400 font-medium">Selected Plan</p>
            <p className="text-sm font-semibold text-indigo-700">{selectedPlan.label}</p>
          </div>
        </div>
      )}

      {/* ── E911 Address ── */}
      <SectionLabel>E911 Address</SectionLabel>
      <div className="bg-gray-50 rounded-xl p-3 border mb-1">
        <div className="grid grid-cols-2 gap-x-3">
          <Field label="Street 1" name="streeT1" value={form.e911ADDRESS.streeT1} onChange={chAddr} placeholder="123 Main St" />
          <Field label="Street 2" name="streeT2" value={form.e911ADDRESS.streeT2} onChange={chAddr} placeholder="Apt / Suite (optional)" />
          <Field label="City"     name="city"    value={form.e911ADDRESS.city}    onChange={chAddr} placeholder="New York" />
          <Field label="State"    name="state"   value={form.e911ADDRESS.state}   onChange={chAddr} placeholder="NY" />
          <div className="col-span-2">
            <Field label="ZIP" name="zip" value={form.e911ADDRESS.zip} onChange={chAddr} placeholder="10001" />
          </div>
        </div>
      </div>

      {/* ── Result banner ── */}
      {result && (
        <div className={`mt-4 rounded-xl border px-4 py-3 flex items-start gap-3 ${result.ok ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
          <span className="text-xl mt-0.5">{result.ok ? "✅" : "⚠️"}</span>
          <div className="flex-1">
            <p className={`text-sm font-semibold ${result.ok ? "text-green-700" : "text-red-600"}`}>{result.msg}</p>
            {result.ok && result.purchaseId && (
              <p className="text-xs text-green-600 mt-1">
                Purchase ID: <span className="font-mono font-bold bg-green-100 px-1.5 py-0.5 rounded">{result.purchaseId}</span>
              </p>
            )}
            {result.timestamp && (
              <p className="text-xs text-gray-400 mt-1">at {formatTimestamp(result.timestamp)}</p>
            )}
          </div>
        </div>
      )}

      <ModalFooter onClose={onClose} onSubmit={handleSubmit} loading={loading} label="Purchase Plan" color="bg-indigo-600" />
    </Modal>
  );
};

// ─── Main Table ───────────────────────────────────────────────────────────────

const MODAL_MAP = {
  swap:       SwapModal,
  deactivate: DeactivateModal,
  reconnect:  ReconnectModal,
  usage:      UsageModal,
  purchase:   PurchasePlanModal,
};

const ActionBtn = ({ label, type, item, color, setModal }) => (
  <button
    onClick={() => setModal({ type, item })}
    className={`rounded-full px-3 py-1 text-xs font-medium text-white ${color} hover:opacity-80 transition`}
  >
    {label}
  </button>
);

const Uspwgpurchase = () => {
  const [data, setData]                 = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [modal, setModal]               = useState(null);
  const [showCoverage, setShowCoverage] = useState(false);

  useEffect(() => {
    fetch(`${BASE}/GetActivatedMDNs`, { headers: { "accept": "*/*", "X-API-Key": API_KEY } })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setData(Array.isArray(d.data) ? d.data : []);
        else setError("API returned failure");
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="p-6 text-gray-500">Loading…</p>;
  if (error)   return <p className="p-6 text-red-500">Error: {error}</p>;

  const ActiveModal = modal && MODAL_MAP[modal.type];

  return (
    <div>
      {/* ── Top toolbar ── */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
        <p className="text-sm font-medium text-gray-600">Activated MDNs</p>
        <button
          onClick={() => setShowCoverage(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition shadow-sm"
        >
          <span>📡</span> Get Coverage
        </button>
      </div>

      {/* ── Table ── */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b">
              {["Activated By", "Activated Date", "ESN", "MDN", "Billing Code", "Plan ID", "Actions"].map((h) => (
                <th key={h} className="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, i) => (
              <tr key={i} className="border-b hover:bg-gray-50 transition">
                <td className="py-3 px-4 text-center">{safe(item?.activatedBy)}</td>
                <td className="py-3 px-4 text-center text-gray-500">{formatDate(item?.activatedDate)}</td>
                <td className="py-3 px-4 text-center font-mono text-xs">{safe(item?.esn)}</td>
                <td className="py-3 px-4 text-center">{safe(item?.mdn)}</td>
                <td className="py-3 px-4 text-center">{safe(item?.billingCode)}</td>
                <td className="py-3 px-4 text-center">{safe(item?.planId)}</td>
                <td className="py-3 px-4">
                  <div className="flex gap-1.5 justify-center flex-wrap">
                    <ActionBtn label="Swap"          type="swap"       item={item} color="bg-blue-500"   setModal={setModal} />
                    <ActionBtn label="Deactivate"    type="deactivate" item={item} color="bg-red-500"    setModal={setModal} />
                    <ActionBtn label="Reconnect"     type="reconnect"  item={item} color="bg-green-500"  setModal={setModal} />
                    <ActionBtn label="Usage"         type="usage"      item={item} color="bg-purple-500" setModal={setModal} />
                    <ActionBtn label="Purchase Plan" type="purchase"   item={item} color="bg-indigo-500" setModal={setModal} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Modals ── */}
      {ActiveModal  && <ActiveModal item={modal.item} onClose={() => setModal(null)} />}
      {showCoverage && <CoverageModal onClose={() => setShowCoverage(false)} />}
    </div>
  );
};

export default Uspwgpurchase;